import { createAction } from 'redux-actions'
import log from 'blazar-sentry'
import * as contentful from 'contentful'
import get from 'lodash/get'
import { GraphQLClient } from 'graphql-request'
import featuresPlugin from '@blazar-plugins/features'
import HttpsProxyAgent from 'https-proxy-agent'
import { actionCreators as api } from 'reduxBits/api'
import queries from './queries'
const prefix = 'contentful'

export const SET_WEB_VIEW_STATUS = `setting web view status`
const setWebViewStatus = createAction(SET_WEB_VIEW_STATUS)

export const REQUEST_ENTRY = `${prefix} request entry`
export const RECEIVE_ENTRY = `${prefix} get entry`

export const TRIGGER_CONTENT_PAGE_ANALYTICS = `${prefix} trigger content page analytics`
export const TRIGGER_CONTENT_STARTPAGE_ANALYTICS = `${prefix} trigger content start page analytics`
const triggerContentPageTracking = createAction(TRIGGER_CONTENT_PAGE_ANALYTICS)
const triggerContentStartPageTracking = createAction(TRIGGER_CONTENT_STARTPAGE_ANALYTICS)

const receiveEntry = createAction(RECEIVE_ENTRY)
const requestEntry = createAction(REQUEST_ENTRY)

export const REQUEST_START_PAGE_ENTRY = `${prefix} request start page entry`
export const RECEIVE_START_PAGE_ENTRY = `${prefix} receive start page entry`
export const ERROR_START_PAGE_ENTRY = `${prefix} error in requesting page entry`

export const REQUEST_CONTENT_PAGE_ENTRY = `${prefix} request page entry`
export const RECEIVE_CONTENT_PAGE_ENTRY = `${prefix} receive page entry`
export const ERROR_CONTENT_PAGE_ENTRY = `${prefix} error in requesting page entry`

export const REQUEST_MENU_ENTRY = `${prefix} request menu entry`
export const RECEIVE_MENU_ENTRY = `${prefix} receive menu entry`
export const ERROR_MENU_ENTRY = `${prefix} error in requesting menu entry`

const requestStartPageEntry = createAction(REQUEST_START_PAGE_ENTRY)
const receiveStartPageEntry = createAction(RECEIVE_START_PAGE_ENTRY)
const errorStartPageEntry = createAction(ERROR_START_PAGE_ENTRY)

const requestContentPageEntry = createAction(REQUEST_CONTENT_PAGE_ENTRY)
const receiveContentPageEntry = createAction(RECEIVE_CONTENT_PAGE_ENTRY)
const errorContentPageEntry = createAction(ERROR_CONTENT_PAGE_ENTRY)

const requestMenuEntry = createAction(REQUEST_MENU_ENTRY)
const receiveMenuEntry = createAction(RECEIVE_MENU_ENTRY)
const errorMenuEntry = createAction(ERROR_MENU_ENTRY)

export const API_TYPE = 'api type'
const apiType = createAction(API_TYPE)

let client
const fetchEntry = (url) => (dispatch, getState) => {
  const state = getState()
  if (!client) {
    client = contentful.createClient({
      space: get(state, 'config.contentful.communication.id'),
      accessToken: get(state, 'config.contentful.communication.deliveryToken'),
    })
  }

  dispatch(requestEntry())

  client
    .getEntries({
      'fields.url': url,
      content_type: 'infoPages',
    })
    .then((entries) => {
      const entry = get(entries, 'items[0].fields')
      dispatch(receiveEntry(entry))
    })
    .catch((error) => dispatch(receiveEntry(error)))
}

const requestContentEntry = (dispatch, queryParam) => {
  switch (queryParam) {
    case '/':
      dispatch(requestStartPageEntry())
      break
    case 'contentNavigation':
      dispatch(requestMenuEntry())
      break
    default:
      dispatch(requestContentPageEntry())
      break
  }
}

const receiveContentEntry = (dispatch, queryParam, payload) => {
  switch (queryParam) {
    case '/':
      dispatch(receiveStartPageEntry(payload))
      break
    case 'contentNavigation':
      dispatch(receiveMenuEntry(payload))
      break
    default:
      dispatch(receiveContentPageEntry(payload))
      break
  }
}

const errorContentEntry = (dispatch, queryParam, err) => {
  switch (queryParam) {
    case '/':
      dispatch(errorStartPageEntry(err))
      break
    case 'contentNavigation':
      dispatch(errorMenuEntry(err))
      break
    default:
      dispatch(errorContentPageEntry(err))
      break
  }
}

export const fetchContentfulData = (queryParam) => async (dispatch, getState) => {
  requestContentEntry(dispatch, queryParam)

  const state = getState()
  const previewParam = get(state, 'router.location.query.preview', 'false')
  const isContentApiEnabled = featuresPlugin.selectors.isEnabled(state, 'contentapi')
  const isPreviewEnabled = featuresPlugin.selectors.isEnabled(state, 'previewContent')
  const showPreviewContent = previewParam.toLowerCase() === 'true' && isPreviewEnabled

  if (!isContentApiEnabled) {
    const spaceID = get(state, 'config.contentful.content.id')
    const accessToken = get(state, 'config.contentful.content.deliveryToken')
    const endpoint = `https://graphql.contentful.com/content/v1/spaces/${spaceID}?locale=en-US`
    const agent = await new HttpsProxyAgent(process.env.HTTPS_PROXY)
    const graphqlClient = new GraphQLClient(endpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      agent: !process.browser ? agent : null,
    })
    const query =
      queryParam === 'contentNavigation'
        ? queries.menuCollectionQuery(queryParam)
        : queries.pageCollectionQuery(queryParam)

    dispatch(apiType('graphql-api'))
    try {
      const graphQLresponse = await graphqlClient.request(query)
      const { pageCollection, menuCollection } = graphQLresponse
      const pageEntry = get(pageCollection, 'items[0]')
      const menuEntry = get(menuCollection, 'items[0]')
      const payload = queryParam === 'contentNavigation' ? menuEntry : pageEntry
      if (payload) {
        receiveContentEntry(dispatch, queryParam, payload)
      } else {
        errorContentEntry(dispatch, queryParam, '404')
      }
    } catch (error) {
      const statusCode = get(error, 'response.status', 500)
      errorContentEntry(dispatch, queryParam, String(statusCode))
      log.error(`fetching graphql content failed with ${JSON.stringify(error.response)}`)
    }
  } else {
    dispatch(apiType('contentapi'))
    const fetchApi = (path) =>
      dispatch(api.fetch(`content/${path}`, { credentials: 'same-origin' }))
    const pathname = queryParam.split('?')[0]
    const path =
      queryParam === 'contentNavigation'
        ? `menus?id=${pathname}&preview=${showPreviewContent}`
        : `pages?id=${pathname}&preview=${showPreviewContent}`

    try {
      const response = await fetchApi(path)
      if (!response.ok) {
        errorContentEntry(dispatch, queryParam, String(response.status))
        log.error(`fetching ${response.url} failed: ${response}`)
        return
      }

      receiveContentEntry(dispatch, queryParam, response.textOrJson)
    } catch (error) {
      log.error(`fetching content failed with ${error}`)
      errorContentEntry(dispatch, queryParam, String(520))
    }
  }
}

export const triggerAnalytics = (pageUrl) => (dispatch) => {
  if (pageUrl === 'start') {
    dispatch(triggerContentStartPageTracking())
  } else {
    dispatch(triggerContentPageTracking())
  }
}

export const actionCreators = {
  setWebViewStatus,
  fetchEntry,
  triggerAnalytics,
  fetchContentfulData,
}
